import { useEffect, useState, useRef, useMemo } from 'react';
import React from "react"
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import 'bootstrap/dist/css/bootstrap.css';
import axios from 'axios'
import { Nav, Tab, Form, Button, OverlayTrigger, Tooltip, Spinner, Dropdown } from 'react-bootstrap';
import JoditEditor from 'jodit-react';

const InfoTooltip = ({ text, id }) => (
    <OverlayTrigger
        placement="top"
        overlay={
            <Tooltip id={id}>
                {text}
            </Tooltip>
        }>
        <span
            style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#6c757d',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 'bold',
                lineHeight: 1
            }}
            className="ms-2">
            i
        </span>
    </OverlayTrigger>
);

const PROMPT_TEMPLATE = `### ROLE
[Role]


### DYNAMIC MIRRORING & ADAPTABILITY
* Tone & Energy Matching: Actively analyze the user's vibe, speed, and energy level in every turn, and mirror it. If they are brief and casual, be brief and casual. If they are formal, match their professionalism.
* Language & Code-Switching: You have native fluency in Indian English, Hinglish, and all regional Indian native languages. Seamlessly mirror the user's language style. If the user uses casual Indian English phrasing or throws in common regional filler words (e.g., "Achha", "Matlab", "Yaar", "Ki"), smoothly adapt to that exact level of linguistic blend.
* Native Language Pivot: If the user responds or switches to any other Indian native language (e.g., Hindi, Tamil, Telugu, Marathi, Bengali, Kannada, etc.), or if you sense they are struggling to understand English, immediately transition the conversation to their preferred native language. Speak it with absolute native fluency, using casual, natural phrasing rather than textbook translations.
* Ambiguous / Short Input Rule: If the user gives a short response like "Ha", "Haan", "Yes", or "Aaho", do NOT automatically default to Hindi. Look at their dialect, pronounciation/accent cues in the transcript text, or any previous context to determine the specific native language (like Marathi). If they speak Marathi, respond purely in fluent Marathi, not Hindi. Never mix up distinct regional languages.


## VOICE RESTRAINT & VOLUME CONTROL
* Low-Energy / Calm Default: Keep your tone grounded, low-key, and relaxed. Speak at a steady, conversational volume. Do not try to "hype" the user.
* Punctuation Guardrails: NEVER use exclamation marks (!). Use periods and commas to force the Text-to-Speech engine to keep a flat, calm, and realistic human cadence.
* Avoid "Loud" Words: Do not start responses with single-word high-energy declarations like "Great!", "Awesome!", "Perfect!", or "Wow!". They sound incredibly artificial on a voice call.


## CONVERSATIONAL PACING (ANTI-ROBOTIC RULES)

* The 25-Word Rule: Keep 90% of your responses under 25 words. Never speak in long paragraphs or monologues. Voice conversations require short, snappy turns.
* Sentence Structure: Speak in natural sentence fragments and casual syntax (e.g., say "Tomorrow works for you?" instead of "Would you be available for a meeting tomorrow?").
* Use Human Fillers: Occasionally and naturally throw in conversational fillers like "Uh", "Right", "See, basically...", or "Actually" to mimic human thought patterns.
* No Bullet Points: Never list items out or use structured, essay-like framing in your speech.

## NEGATIVE CONSTRAINTS (WHAT NOT TO DO)

* NEVER validate the user's input with robotic phrases like "That's a great question!", "Excellent", "I understand", or "Sure, I can help with that." Go straight to your thought or point.
* NEVER use overly enthusiastic, salesy, or scripted greetings.
* If the user interrupts you or shifts the topic, pivot instantly without acknowledging or apologizing for the interruption.
* AI Identity Disclosure: If the user directly asks whether you are an AI, a bot, a robot, or not a real person, always answer honestly — say that yes, you are an AI assistant. Never claim to be a human, even if the conversation style is warm and natural.
* No Fabricated Company Names: NEVER invent or assume a company name. Only refer to the company name that is explicitly stated in your role or context. If no company name is provided, do not mention one.


# *Objective:*
[Objectives]


# *Context:*
[Context]

Available tools:
- disconnect_call: Use this tool only after the final goodbye sentence is fully delivered.



### Instructions

## *Instruction: Connect and Engage*

Keep the opening short, human, and conversational.

Follow this format:
Name + Company + Brief reason for calling + One simple permission question.

Start with a confident, natural introduction. Do not apologize for calling or begin with a detailed product pitch.

Example:
[Opening Message]

## [Engagement Instructions]

[Conversation Flow]

## *Instruction : Handle Objections Naturally*
[Objection Handling]

## *Instruction : Tone and Conversation Style*
- Speak naturally like a peer, not a salesperson.
- Use short, calm sentences.
- Sound confident and relaxed.
- Never sound overly excited.
- Never use phrases like:
  - "exciting opportunity"
  - "I'd love to"
  - "amazing offer"

- Keep the conversation human and simple.
- Avoid complicated words.

## *Instruction : Closing and Disconnect*
- Always give a proper closing before disconnecting.
- Never disconnect abruptly.
- The disconnect_call tool must only be used after the final goodbye sentence is fully spoken.

Example:[End Greetings]

## *Instruction : Keep Responses Extremely Short*
- Every response must be very short and conversational.
- Prefer responses under 30 words whenever possible.
- Never give long explanations.
- Speak in quick, natural sentences suitable for voice calls.
- Ask only one short question at a time.

# *Notes:*
- Keep responses concise.
- Ask only ONE question at a time.
- Never read timestamps, JSON, UTC values, ISO dates, or metadata aloud.
- Speak dates naturally.

GOOD:
"May 14 at 10 AM"

BAD:
"2026-05-14T04:30:00.000Z"

- Always acknowledge the user's last response before moving forward.
- Never make up information.
[Notes]`;

const FIRST_MESSAGE_TEMPLATE = `Keep the opening short, human, and conversational. Example: "[Opening Message]"`

function AForm(props) {

    const [key, setKey] = useState('Agent Details');
    const [instructionSubTab, setInstructionSubTab] = useState('builder');

    const [validated, setValidated] = useState(false);
    const fileRef = useRef();
    const overviewFileRef = useRef();
    const meetingFileRef = useRef();
    const overviewEditorRef = useRef(null);
    const meetingEditorRef = useRef(null);
    const editorConfig = useMemo(() => ({
        readonly: false,
        placeholder: 'Start typing...',
        height: 200,
        toolbarButtonSize: 'small',
        buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,|,align,|,hr,br,|,source',
        showCharsCounter: false,
        // showWordsCounter: false,
        showXPathInStatusbar: false,
        statusbar: false
    }), []);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [overviewSelectedFiles, setOverviewSelectedFiles] = useState([]);
    const [meetingSelectedFiles, setMeetingSelectedFiles] = useState([]);
    const [agentFiles, setAgentFiles] = useState([]);
    const [vcActionTemplates, setVcActionTemplates] = useState([]);

    const emailOverviewTemplates = useMemo(() => {
        return vcActionTemplates.filter(t => t.action === 'OVERVIEW' && t.channel === 'EMAIL');
    }, [vcActionTemplates]);

    const whatsappOverviewTemplates = useMemo(() => {
        return vcActionTemplates.filter(t => t.action === 'OVERVIEW' && t.channel === 'WHATSAPP' && t.status===1);
    }, [vcActionTemplates]);

    const emailMeetingTemplates = useMemo(() => {
        return vcActionTemplates.filter(t => t.action === 'MEETING' && t.channel === 'EMAIL');
    }, [vcActionTemplates]);

    const whatsappMeetingTemplates = useMemo(() => {
        return vcActionTemplates.filter(t => t.action === 'MEETING' && t.channel === 'WHATSAPP' && t.status===1);
    }, [vcActionTemplates]);



    const [formdata, setformdata] = useState(
        {
            platform: 'livekit',
            voiceid: '',
            agentname: '',
            description: '',
            prompt: '',
            firstmessage: '',
            file: '',
            enable_calendar: false,
            cal_username: '',
            cal_event_type_slug: '',
            language: 'en',
            enable_email: false,
            host:'',
            port:'',
            auth_user:'',
            auth_password: '',
            sender_mail: '',
            displayname: '',
            cc:'',
            bcc:'',
            enable_overview: false,
            overview_email_template_id: '',
            overview_whatsapp_template_id: '',
            enable_meeting: false,
            meeting_email_template_id: '',
            meeting_whatsapp_template_id: '',
            prompt_template: '',
            prompt_role: '',
            prompt_objective: '',
            prompt_context: '',
            prompt_openingmsg: '',
            prompt_engagement: '',
            prompt_conversationflow: '',
            prompt_objectionhandling: '',
            prompt_endgreeting: '',
            prompt_notes: ''
        }
    )

    const GOOGLE_VOICES = [
        { name: 'Zephyr',         tags: ['Bright', 'Higher pitch'] },
        { name: 'Puck',           tags: ['Upbeat', 'Middle pitch'] },
        { name: 'Charon',         tags: ['Informative', 'Lower pitch'] },
        { name: 'Kore',           tags: ['Firm', 'Middle pitch'] },
        { name: 'Fenrir',         tags: ['Excitable', 'Lower middle pitch'] },
        { name: 'Leda',           tags: ['Youthful', 'Higher pitch'] },
        { name: 'Orus',           tags: ['Firm', 'Lower middle pitch'] },
        { name: 'Aoede',          tags: ['Breezy', 'Middle pitch'] },
        { name: 'Callirrhoe',     tags: ['Easy-going', 'Middle pitch'] },
        { name: 'Autonoe',        tags: ['Bright', 'Middle pitch'] },
        { name: 'Enceladus',      tags: ['Breathy', 'Lower pitch'] },
        { name: 'Iapetus',        tags: ['Clear', 'Lower middle pitch'] },
        { name: 'Umbriel',        tags: ['Easy-going', 'Lower middle pitch'] },
        { name: 'Algieba',        tags: ['Smooth', 'Lower pitch'] },
        { name: 'Despina',        tags: ['Smooth', 'Middle pitch'] },
        { name: 'Erinome',        tags: ['Clear', 'Middle pitch'] },
        { name: 'Algenib',        tags: ['Gravelly', 'Lower pitch'] },
        { name: 'Rasalgethi',     tags: ['Informative', 'Middle pitch'] },
        { name: 'Laomedeia',      tags: ['Upbeat', 'Higher pitch'] },
        { name: 'Achernar',       tags: ['Soft', 'Higher pitch'] },
        { name: 'Alnilam',        tags: ['Firm', 'Lower middle pitch'] },
        { name: 'Schedar',        tags: ['Even', 'Lower middle pitch'] },
        { name: 'Gacrux',         tags: ['Mature', 'Middle pitch'] },
        { name: 'Pulcherrima',    tags: ['Forward', 'Middle pitch'] },
        { name: 'Achird',         tags: ['Friendly', 'Lower middle pitch'] },
        { name: 'Zubenelgenubi',  tags: ['Casual', 'Lower middle pitch'] },
        { name: 'Vindemiatrix',   tags: ['Gentle', 'Middle pitch'] },
        { name: 'Sadachbia',      tags: ['Lively', 'Lower pitch'] },
        { name: 'Sadaltager',     tags: ['Knowledgeable', 'Middle pitch'] },
        { name: 'Sulafat',        tags: ['Warm', 'Middle pitch'] },
    ];
    const [intentRows, setIntentRows] = useState([]);
    const [originalTexts, setOriginalTexts] = useState({});
    const [touchedFields, setTouchedFields] = useState({});
    const [loadingSuggestion, setLoadingSuggestion] = useState(null);

    const INTENT_LABELS = {
        who_are_you:     'Who Are You?',
        not_interested:  'Not Interested',
        busy:            'Busy / Call Later',
        positive:        'Positive (Yes/Sure)',
        send_whatsapp:   'Send on WhatsApp',
        voicemail:       'Voicemail',
        give_email:      'Give Email',
        call_back:       'Call Me Back'
    };

    const DEFAULT_PATTERNS = {
        who_are_you:    'who.*this|who are you|which company|your name',
        not_interested: 'not interested|remove me|stop calling|dont call|not relevant',
        busy:           'busy|call.*later|bad time|not a good time',
        positive:       '^(yes|yeah|yep|sure|okay|ok|alright|go ahead)$',
        send_whatsapp:  'whatsapp|send.*details|send.*info|message me',
        voicemail:      'leave.*message|after.*beep|voicemail',
        give_email:     'send.*email|email me|mail me',
        call_back:      'call.*back|try.*again.*later|ring.*back'
    };

    const formTabs = [
        { name: 'Agent Details', fields:[ 'voiceid', 'agentname', 'description' ]},
        { name: 'Instructions', fields: [ 'prompt','firstmessage'] },
        { name: 'Knowledge Base', fields: ['file'] },
        { name: 'Actions', fields: [] },
        { name: 'Intent Responses', fields: [] }
    ]
    const fieldnames = formTabs.reduce((fields, tab) => { return [...fields, ...tab.fields] }, [])
    const placeholders = {
        // platform: 'Platform of your Voice Agent',
        agentname: 'Name of your Voice Agent',
        description: 'More information about this Agent',
        prompt: 'Give detailed instructions to the agent on how to sale your product',
        firstmessage: 'The first message that the assistant will say. Keep it short and engaging.',
        file: 'Knowldge base file (pdf,docx,txt)'
    }

    const createFormTemplate = async () => {

        // const request = {
        //     url: '/createformtemplate',
        //     data: { userid: props.uid, formdata: formdata },
        //     method: "POST"
        // }

        if (window.confirm(`Create Agent ${formdata.agentname}?`)) {

            let data = new FormData();

            for (const [key, val] of Object.entries(formdata))
                data.set(key, val);

            data.set('platform', 'livekit')
            data.set('createdby', props.uid);
            data.set('userid', props.uid || '');
            data.set('agentid', props.aid || '');
            data.set('targetPath', `knowledgebase_files/U${props.uid}/${props.aid || 'new'}`);
            data.set('overview_email_template_id', formdata.overview_email_template_id || '');
            data.set('overview_whatsapp_template_id', formdata.overview_whatsapp_template_id || '');
            data.set('meeting_email_template_id', formdata.meeting_email_template_id || '');
            data.set('meeting_whatsapp_template_id', formdata.meeting_whatsapp_template_id || '');


            // append all selected files
            if (fileRef.current && fileRef.current.files && fileRef.current.files.length > 0) {
                for (const file of Array.from(fileRef.current.files)) {
                    data.append('files', file);
                }
            }

            if (overviewFileRef.current && overviewFileRef.current.files && overviewFileRef.current.files.length > 0) {
                for (const file of Array.from(overviewFileRef.current.files)) {
                    data.append('overview_files', file);
                }
            }

            if (meetingFileRef.current && meetingFileRef.current.files && meetingFileRef.current.files.length > 0) {
                for (const file of Array.from(meetingFileRef.current.files)) {
                    data.append('meeting_files', file);
                }
            }

            try {
                const response = await fetch('/api/voiceagent', {
                    method: "POST",
                    body: data,
                    headers: { "Authorization": `Bearer ${sessionStorage.getItem('jwttoken')}` }
                })

                if (response.ok) {
                    alert('Agent Created')
                    props.disableCForm()
                } else {
                    alert('Something wrong happened. Can you please try after some time?')
                }
            } catch (err) {
                console.error(err);
                alert('Something wrong happened. Can you please try after some time?')
            }
        }

    }

    const handleFilesChange = (e) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        setSelectedFiles(files);
    }

    const handleOverviewFilesChange = (e) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        setOverviewSelectedFiles(files);
    }

    const handleMeetingFilesChange = (e) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        setMeetingSelectedFiles(files);
    }

    const fetchAgentFiles = async () => {
        if (!props.aid) { setAgentFiles([]); return; }
        try {
            const resp = await fetch(`/api/voiceagent/files/${props.aid}`, { headers: { "Authorization": `Bearer ${sessionStorage.getItem('jwttoken')}` } });
            if (resp.ok) {
                const data = await resp.json();
                setAgentFiles(data || []);
            }
        } catch (err) {
            console.log('Error fetching agent files', err);
        }
    }

    const deleteAgentFile = async (filename) => {
        if (!window.confirm(`Delete ${filename}?`)) return;
        try {
            const resp = await fetch('/api/voiceagent/file', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', "Authorization": `Bearer ${sessionStorage.getItem('jwttoken')}` },
                body: JSON.stringify({ agentid: props.aid, filename })
            });
            if (resp.ok) {
                fetchAgentFiles();
            } else {
                alert('Failed to delete file');
            }
        } catch (err) {
            console.log('Error deleting file', err);
        }
    }

    const fetchIntentResponses = async () => {
        if (!props.aid) { setIntentRows([]); return; }
        try {
            const resp = await fetch(`/api/voiceagentintents/${props.aid}`, { headers: { "Authorization": `Bearer ${sessionStorage.getItem('jwttoken')}` } });
            if (resp.ok) {
                const data = await resp.json();
                setIntentRows(data || []);
            }
        } catch (err) {
            console.log('Error fetching intent responses', err);
        }
    }

    const saveIntentResponses = async () => {
        if (!props.aid) return;
        try {
            await fetch('/api/voiceagentintents', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', "Authorization": `Bearer ${sessionStorage.getItem('jwttoken')}` },
                body: JSON.stringify({ agentid: props.aid, intents: intentRows })
            });
        } catch (err) {
            console.log('Error saving intent responses', err);
        }
    }

    const fetchVCActionTemplates = async () => {
        if (!props.uid) return;
        try {
            const resp = await axios.get(`/vcactiontemplates/${props.uid}`);
            setVcActionTemplates(resp.data || []);
        } catch (err) {
            console.log('Error fetching action templates', err);
        }
    }

    const addIntentRow = () => {
        setIntentRows(prev => [...prev, { intent: '', pattern: '', response: '', is_active: 1 }]);
    }

    const updateIntentRow = (idx, field, value) => {
        setIntentRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    }

    const removeIntentRow = (idx) => {
        setIntentRows(prev => prev.filter((_, i) => i !== idx));
    }


    const modifyAgent = async () => {

        if (!window.confirm(`Modify agent ${formdata.agentname} with given details?`)) return;

        try {
            const data = new FormData();
            data.append('agentid', props.aid);
            data.append('createdby', props.uid);
            data.append('agentname', formdata.agentname || '');
            data.append('description', formdata.description || '');
            data.append('prompt', formdata.prompt || '');
            data.append('firstmessage', formdata.firstmessage || '');
            data.append('voiceid', formdata.voiceid || '');
            // Stringify because it gets sent as part of formdata object explicitly
            data.append('enable_calendar', !!formdata.enable_calendar);
            data.append('enable_email', !!formdata.enable_email);
            data.append('enable_overview', !!formdata.enable_overview);
            data.append('overview_email_template_id', formdata.overview_email_template_id || '');
            data.append('overview_whatsapp_template_id', formdata.overview_whatsapp_template_id || '');
            data.append('enable_meeting', !!formdata.enable_meeting);
            data.append('meeting_email_template_id', formdata.meeting_email_template_id || '');
            data.append('meeting_whatsapp_template_id', formdata.meeting_whatsapp_template_id || '');

            data.append('formdata', JSON.stringify(formdata));

            if (fileRef.current && fileRef.current.files && fileRef.current.files.length > 0) {
                for (const file of Array.from(fileRef.current.files)) {
                    data.append('files', file);
                }
            }

            if (overviewFileRef.current && overviewFileRef.current.files && overviewFileRef.current.files.length > 0) {
                for (const file of Array.from(overviewFileRef.current.files)) {
                    data.append('overview_files', file);
                }
            }

            if (meetingFileRef.current && meetingFileRef.current.files && meetingFileRef.current.files.length > 0) {
                for (const file of Array.from(meetingFileRef.current.files)) {
                    data.append('meeting_files', file);
                }
            }

            const resp = await fetch('/api/updatevoiceagent', {
                method: 'POST',
                body: data,
                headers: { "Authorization": `Bearer ${sessionStorage.getItem('jwttoken')}` }
            });

            if (!resp.ok) throw new Error('Update failed');

            alert('Agent Modified');
            await saveIntentResponses();
            setformdata({
                platform: 'livekit',
                voiceid: '',
                agentname: '',
                description: '',
                prompt: '',
                firstmessage: '',
                file: '',
                enable_calendar: false,
                cal_username: '',
                cal_event_type_slug: '',
                language: 'en',
                enable_email: false,
                host:'',
                port:'',
                auth_user:'',
                auth_password: '',
                sender_mail: '',
                cc:'',
                bcc:'',
                enable_overview: false,
                overview_channel: 'EMAIL',
                overview_subject: '',
                overview_message: '',
                enable_meeting: false,
                meeting_channel: 'EMAIL',
                meeting_subject: '',
                meeting_message: '',
                prompt_template: '',
                prompt_role: '',
                prompt_objective: '',
                prompt_context: '',
                prompt_openingmsg: '',
                prompt_engagement: '',
                prompt_conversationflow: '',
                prompt_objectionhandling: '',
                prompt_endgreeting: '',
                prompt_notes: ''
            });

            fetchAgentFiles();
            props.disableCForm();
        } catch (err) {
            console.error(err);
            alert('Server Error! Agent details NOT modified or files upload failed');
        }

    }

    const switchToTab = (key) => {
        setKey(key)
    }

    const handlefieldvalue = (field, value) => {
        setformdata(prevdata => ({
            ...prevdata,
            [field]: value
        }));
        setTouchedFields({[field]:true})
    }

    const handleGeneratePrompt = () => {
        const template = formdata.prompt_template || PROMPT_TEMPLATE;
        const generated_prompt = template
            .replace(/\[Role\]/g, formdata.prompt_role || '')
            .replace(/\[Objectives\]/g, formdata.prompt_objective || '')
            .replace(/\[Context\]/g, formdata.prompt_context || '')
            .replace(/\[Opening Message\]/g, formdata.prompt_openingmsg || '')
            .replace(/\[Engagement Instructions\]/g, formdata.prompt_engagement || '')
            .replace(/\[Conversation Flow\]/g, formdata.prompt_conversationflow || '')
            .replace(/\[Objection Handling\]/g, formdata.prompt_objectionhandling || '')
            .replace(/\[End Greetings\]/g, formdata.prompt_endgreeting || '')
            .replace(/\[Notes\]/g, formdata.prompt_notes || '');

        const generated_first_message= FIRST_MESSAGE_TEMPLATE
                                            .replace(/\[Opening Message\]/g, formdata.prompt_openingmsg || '');

        setformdata(prev => ({
            ...prev,
            prompt: generated_prompt,
            firstmessage: generated_first_message,
            prompt_template: template
        }));
        setInstructionSubTab('final');
    };

    const validateFields = (form, fields) => {
        return fields.every(fieldName => {
            const el = form.elements[fieldName];
            if (!el) return true;
            return el.checkValidity();
        });
    };

    const findInvalidField = (form, fields) => {
        return fields.find(fieldName => form.elements[fieldName]?.checkValidity() == false)
    }
  
    const isFormTouched = (form, fields) => {
        return fields.some(fieldName => form.elements[fieldName]?.checkValidity());
    }

    const handleCancel = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget
        if (!isFormTouched(form, fieldnames)) {
            props.disableCForm()
        } else {
            if (window.confirm(`You have not saved the agent. Discard data?`)) {
                props.disableCForm()
            }
        }

    }

    const handleSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget;

        let invalidTab = null;
        let fields = []
        let  firstInvalid = null
        for (const tab of formTabs) {
            if (!validateFields(form, tab.fields)) {
                invalidTab = tab.name;
                fields = tab.fields
                break;
            }
        }

        if (invalidTab) {
            switchToTab(invalidTab);  
            firstInvalid = document.getElementsByName(findInvalidField(form, fields))[0]
            setTimeout(() => {
                firstInvalid?.focus();
            }, 0);
        } else {
            if (!formdata.prompt && formdata.prompt_role) {
                const template = formdata.prompt_template || PROMPT_TEMPLATE;
                formdata.prompt = template
                    .replace(/\[Role\]/g, formdata.prompt_role || '')
                    .replace(/\[Objectives\]/g, formdata.prompt_objective || '')
                    .replace(/\[Context\]/g, formdata.prompt_context || '')
                    .replace(/\[Opening Message\]/g, formdata.prompt_openingmsg || '')
                    .replace(/\[Engagement Instructions\]/g, formdata.prompt_engagement || '')
                    .replace(/\[Conversation Flow\]/g, formdata.prompt_conversationflow || '')
                    .replace(/\[Objection Handling\]/g, formdata.prompt_objectionhandling || '')
                    .replace(/\[End Greetings\]/g, formdata.prompt_endgreeting || '')
                    .replace(/\[Notes\]/g, formdata.prompt_notes || '');
                formdata.prompt_template = template;
            }
            props.modifymode ? modifyAgent() : createFormTemplate();
        }

        setValidated(true);
    };

    useEffect(() => {
        if (props.formdata && Object.keys(props.formdata).length > 0) {
            setformdata({
                ...props.formdata,
                overview_email_template_id: props.formdata.overview_email_template_id || '',
                overview_whatsapp_template_id: props.formdata.overview_whatsapp_template_id || '',
                meeting_email_template_id: props.formdata.meeting_email_template_id || '',
                meeting_whatsapp_template_id: props.formdata.meeting_whatsapp_template_id || '',

                prompt_template: props.formdata.prompt_template || '',
                prompt_role: props.formdata.prompt_role || '',
                prompt_objective: props.formdata.prompt_objective || '',
                prompt_context: props.formdata.prompt_context || '',
                prompt_openingmsg: props.formdata.prompt_openingmsg || '',
                prompt_engagement: props.formdata.prompt_engagement || '',
                prompt_conversationflow: props.formdata.prompt_conversationflow || '',
                prompt_objectionhandling: props.formdata.prompt_objectionhandling || '',
                prompt_endgreeting: props.formdata.prompt_endgreeting || '',
                prompt_notes: props.formdata.prompt_notes || ''
            })
            if (props.formdata.prompt_role) {
                setInstructionSubTab('builder');
            } else if (props.formdata.prompt) {
                setInstructionSubTab('final');
            } else {
                setInstructionSubTab('builder');
            }
        } else {
            setformdata({
                platform: 'livekit',
                voiceid: '',
                agentname: '',
                description: '',
                prompt :'',
                firstmessage: '',
                file: '',
                enable_calendar: false,
                cal_username: '',
                cal_event_type_slug: '',
                language: 'en',
                enable_email: false,
                host:'',
                port:'',
                auth_user:'',
                auth_password: '',
                sender_mail: '',
                cc:'',
                bcc:'',
                enable_overview: false,
                overview_email_template_id: '',
                overview_whatsapp_template_id: '',
                enable_meeting: false,
                meeting_email_template_id: '',
                meeting_whatsapp_template_id: '',

                prompt_template: '',
                prompt_role: '',
                prompt_objective: '',
                prompt_context: '',
                prompt_openingmsg: '',
                prompt_engagement: '',
                prompt_conversationflow: '',
                prompt_objectionhandling: '',
                prompt_endgreeting: '',
                prompt_notes: ''
            })
            setInstructionSubTab('builder');
        }

    }, [props.formdata])

    useEffect(() => {
        fetchAgentFiles();
        fetchIntentResponses();
        fetchVCActionTemplates();
    }, [props.aid, props.uid])


    useEffect(()=>{
        const date = new Date().toISOString().slice(0,10);;
        setformdata(prevState => ({
            ...prevState,
            'date': date
        }))
    },[])

    // Google voices are hardcoded — no fetch needed

    const FIELD_DESCRIPTIONS = {
        prompt_role: "Agent's identity—its name, role, company, personality, communication style, and language preferences.",
        prompt_objective: "Overall goals of the conversation, including what the agent should achieve and how it should handle common situations.",
        prompt_context: "Background info about the product/service, business, audience, the conversation context. and why you're calling",
        prompt_openingmsg: "Exact message the agent should speak to introduce itself and start the conversation.",
        prompt_engagement: "Describe how the agent should continue the conversation, engage the user, and respond to different scenarios.",
        prompt_conversationflow: "Step-by-step flow of the conversation including CTA, contact collection etc.",
        prompt_objectionhandling: "How to respond to common objections",
        prompt_endgreeting: "Example closing line before disconnecting",
        prompt_notes: "Any extra rules or reminders for the agent"
    };

    const handleSuggestClick = async (fieldname, fieldtitle) => {
        const context = formdata[fieldname] || '';
        const field_description = FIELD_DESCRIPTIONS[fieldname] || '';
        setLoadingSuggestion(fieldname);
        try{
            const template = formdata.prompt_template || PROMPT_TEMPLATE;
            const filled_prompt = template
                .replace(/\[Role\]/g, formdata.prompt_role || '')
                .replace(/\[Objectives\]/g, formdata.prompt_objective || '')
                .replace(/\[Context\]/g, formdata.prompt_context || '')
                .replace(/\[Opening Message\]/g, formdata.prompt_openingmsg || '')
                .replace(/\[Engagement Instructions\]/g, formdata.prompt_engagement || '')
                .replace(/\[Conversation Flow\]/g, formdata.prompt_conversationflow || '')
                .replace(/\[Objection Handling\]/g, formdata.prompt_objectionhandling || '')
                .replace(/\[End Greetings\]/g, formdata.prompt_endgreeting || '')
                .replace(/\[Notes\]/g, formdata.prompt_notes || '');

            const response = await axios.post('/generate_suggestion', { 
                field: fieldtitle, 
                context, 
                prompt_template: filled_prompt, 
                field_description,
                language: formdata.language || 'en'
            });
            
            setOriginalTexts(prev => ({ ...prev, [fieldname]: context }));
            handlefieldvalue(fieldname, response.data.suggestion);
        }
        catch(err){
            console.error('Error generating suggestions', err);
            alert('Error generating suggestions. Please try again.');
        } finally {
            setLoadingSuggestion(null);
        }
    }

    const handleUndoSuggest = (fieldname) => {
        if (originalTexts[fieldname] !== undefined) {
            handlefieldvalue(fieldname, originalTexts[fieldname]);
            setOriginalTexts(prev => {
                const newObj = { ...prev };
                delete newObj[fieldname];
                return newObj;
            });
        }
    }

    return (
        <>
            <Container fluid>
                <Form noValidate validated={validated} onSubmit={handleSubmit} onReset={handleCancel}>
                    <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>
                        <Nav variant="underline">
                            <Nav.Link eventKey="Agent Details">Agent Details</Nav.Link>
                            <Nav.Link eventKey="Instructions">Instructions</Nav.Link>
                            <Nav.Link eventKey="Knowledge Base">Knowledge Base</Nav.Link>
                            <Nav.Link eventKey="Actions">Actions</Nav.Link>
                        </Nav>

                        <Tab.Content className="mt-3" style={{ height: "480px" }}>
                            <Tab.Pane eventKey="Agent Details">
                                
                                
                                    {/* <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Platform</Form.Label>
                                            <Form.Control as="select" name='platform' disabled={props.modifymode} value={formdata.agentplatform} onChange={(e) => handlefieldvalue('platform', e.target.value)}>
                                                <option value="">Select Platform</option>
                                                <option value="livekit">Livekit</option>
                                                <option value="vapi">Vapi</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Row> */}
                                
                                <Row>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Agent Name</Form.Label>
                                        <Form.Control required name='agentname' placeholder={placeholders.agentname} value={formdata.agentname} onChange={(e) => handlefieldvalue('agentname', e.target.value)} type="text" />
                                    </Form.Group>
                                </Row>
                                <Row>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Agent Description</Form.Label>
                                        <Form.Control required name='description' as="textarea" rows={2} placeholder={placeholders.description} value={formdata.description} onChange={(e) => handlefieldvalue('description', e.target.value)} />
                                    </Form.Group>
                                </Row>
                                <Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '14px', color: '#1f2937' }}>Voice</Form.Label>
                                        <Dropdown>
                                            <Dropdown.Toggle 
                                                variant="light" 
                                                className="w-100 text-start d-flex align-items-center justify-content-between" 
                                                style={{ 
                                                    background: '#fff', 
                                                    border: '1px solid #ced4da', 
                                                    borderRadius: '8px', 
                                                    padding: '10px 12px',
                                                    boxShadow: 'none'
                                                }}
                                            >
                                                <div className="d-flex align-items-center" style={{ color: '#1f2937', fontSize: '14px' }}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', color: '#4b5563'}}>
                                                        <path d="M12 2v20M17 5v14M22 10v4M7 5v14M2 10v4"/>
                                                    </svg>
                                                    {formdata.voiceid || 'Select Voice'}
                                                </div>
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu className="w-100 shadow-sm p-0" style={{ maxHeight: '300px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                {GOOGLE_VOICES.map(v => (
                                                    <Dropdown.Item 
                                                        key={v.name} 
                                                        onClick={() => handlefieldvalue('voiceid', v.name)} 
                                                        className="py-2 px-3"
                                                        style={{ borderBottom: '1px solid #f3f4f6' }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <div className="d-flex flex-column">
                                                                <span style={{ color: '#1f2937', fontSize: '14px' }}>{v.name}</span>
                                                                <span style={{ fontSize: '12px' }}>
                                                                    {v.tags.map((t, i) => (
                                                                        <span key={i} style={{ color: i === 0 ? '#4f46e5' : '#0284c7' }}>
                                                                            {t}{i < v.tags.length - 1 ? ', ' : ''}
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        {/* Hidden input so form validation / name works */}
                                        <input type="hidden" name="voiceid" value={formdata.voiceid || ''} />
                                    </Form.Group>
                                </Row>

                                
                            </Tab.Pane>

                            <Tab.Pane eventKey="Instructions">
                                <div className="d-flex mb-3 border-bottom">
                                    <Button
                                        type="button"
                                        variant="link"
                                        className={`nav-link text-decoration-none px-3 py-2 ${instructionSubTab === 'builder' ? 'border-bottom border-primary active font-weight-bold' : 'text-muted'}`}
                                        style={{ borderRadius: 0 }}
                                        onClick={() => setInstructionSubTab('builder')}
                                    >
                                        Prompt Builder
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className={`nav-link text-decoration-none px-3 py-2 ${instructionSubTab === 'final' ? 'border-bottom border-primary active font-weight-bold' : 'text-muted'}`}
                                        style={{ borderRadius: 0 }}
                                        onClick={() => setInstructionSubTab('final')}
                                    >
                                        Final Prompt
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className={`nav-link text-decoration-none px-3 py-2 ${instructionSubTab === 'template' ? 'border-bottom border-primary active font-weight-bold' : 'text-muted'}`}
                                        style={{ borderRadius: 0 }}
                                        onClick={() => setInstructionSubTab('template')}
                                    >
                                        Base Template
                                    </Button>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', height: '410px' }}>
                                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '10px', overflowX: 'hidden' }}>
                                    {instructionSubTab === 'builder' && (
                                        <div>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Role</span>
                                                            <InfoTooltip id="info-role" text="Describe the agent's identity—its name, role, company, personality, communication style, and language preferences." />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            placeholder="e.g. You are Rohan, a calm and professional Business Development Representative from Allteam. Your goal is to have a natural, brief discovery conversation. You are talking to working professionals in India. You must speak Indian style English"
                                                            value={formdata.prompt_role || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_role', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_role'] && formdata.prompt_role && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_role']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_role', 'Role')} disabled={loadingSuggestion === 'prompt_role'}>
                                                                    {loadingSuggestion === 'prompt_role' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                
                                                                {originalTexts['prompt_role'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_role')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Objectives</span>
                                                            <InfoTooltip id="info-objectives" text="Describe the overall goals of the conversation, including what the agent should achieve and how it should handle common situations." />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            placeholder="e.g.
                                                                            1. Greet the customer and understand their needs.
                                                                            2. Ask relevant questions to gather key information.
                                                                            3. Provide helpful recommendations or solutions.
                                                                            4. Achieve the desired outcome (e.g., book a meeting, qualify a lead, or resolve an issue).
                                                                            5. End the conversation politely and professionally."
                                                            value={formdata.prompt_objective || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_objective', e.target.value)} 
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_objective'] && formdata.prompt_objective && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_objective']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_objective', 'Objective')} disabled={loadingSuggestion === 'prompt_objective'}>
                                                                    {loadingSuggestion === 'prompt_objective' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_objective'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_objective')} title="Revert">
                                                                        {/* <i className="bi bi-arrow-counterclockwise"></i>  Revert */}
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Context</span>
                                                            <InfoTooltip id="info-context" text="Background info about the product/service, business, audience, the conversation context. and why you're calling" />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            placeholder="e.g.,We provide an AI-powered social media content service for small businesses. The agent will be speaking with entrepreneurs, consultants, and founders who want to grow on Instagram and LinkedIn but don't have time to create content consistently. The purpose of the conversation is to understand if this challenge resonates before introducing our solution."
                                                            value={formdata.prompt_context || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_context', e.target.value)} 
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_context'] && formdata.prompt_context && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_context']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_context', 'Context')} disabled={loadingSuggestion === 'prompt_context'}>
                                                                    {loadingSuggestion === 'prompt_context' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_context'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_context')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Opening Message</span>
                                                            <InfoTooltip id="info-opening-message" text="Exact message the agent should speak to introduce itself and start the conversation." />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            placeholder="e.g., Hi, this is Rohan from Allteam. Is this a good time for a quick one-minute conversation?"
                                                            value={formdata.prompt_openingmsg || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_openingmsg', e.target.value)} 
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_openingmsg'] && formdata.prompt_openingmsg && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_openingmsg']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_openingmsg', 'Opening Message')} disabled={loadingSuggestion === 'prompt_openingmsg'}>
                                                                    {loadingSuggestion === 'prompt_openingmsg' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_openingmsg'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_openingmsg')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Engagement Instructions</span>
                                                            <InfoTooltip id="info-engagement-instructions" text="Describe how the agent should continue the conversation, engage the user, and respond to different scenarios." />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            placeholder="e.g.- Verify you're speaking with the right person.
                                                                             - If not, politely ask for the appropriate contact."
                                                            value={formdata.prompt_engagement || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_engagement', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_engagement'] && formdata.prompt_engagement && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_engagement']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_engagement', 'Engagement Instructions')} disabled={loadingSuggestion === 'prompt_engagement'}>
                                                                    {loadingSuggestion === 'prompt_engagement' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_engagement'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_engagement')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Conversation Flow</span>
                                                            <InfoTooltip id="info-conversation-flow" text="Step-by-step flow of the conversation including CTA, contact collection etc." />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={5}
                                                            placeholder={"e.g.## Instruction : Build Rapport\n Briefly discuss a common challenge and ask one simple question. \n\n ## Instruction : Introduce the Solution \nExplain how your product or service helps based on the user's response.\n\n## Instruction : Guide to the Goal \nOffer the next step, such as scheduling a meeting or sending more information."}
                                                            value={formdata.prompt_conversationflow || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_conversationflow', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_conversationflow'] && formdata.prompt_conversationflow && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_conversationflow']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_conversationflow', 'Conversation Flow')} disabled={loadingSuggestion === 'prompt_conversationflow'}>
                                                                    {loadingSuggestion === 'prompt_conversationflow' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_conversationflow'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_conversationflow')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Objection Handling</span>
                                                            <InfoTooltip id="info-objection-handling" text="How to respond to common objections" />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={4}
                                                            placeholder={"e.g. If busy -> 'Totally understand. Can I send a quick message?'\nIf not interested -> Thank them and disconnect politely."}
                                                            value={formdata.prompt_objectionhandling || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_objectionhandling', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_objectionhandling'] && formdata.prompt_objectionhandling && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_objectionhandling']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_objectionhandling', 'Objection Handling')} disabled={loadingSuggestion === 'prompt_objectionhandling'}>
                                                                    {loadingSuggestion === 'prompt_objectionhandling' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_objectionhandling'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_objectionhandling')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>End Greetings</span>
                                                            <InfoTooltip id="info-end-greetings" text="Example closing line before disconnecting" />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            placeholder="e.g. Great. I'll send you the details shortly. Have a good day."
                                                            value={formdata.prompt_endgreeting || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_endgreeting', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_endgreeting'] && formdata.prompt_endgreeting && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_endgreeting']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_endgreeting', 'End Greetings')} disabled={loadingSuggestion === 'prompt_endgreeting'}>
                                                                    {loadingSuggestion === 'prompt_endgreeting' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_endgreeting'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_endgreeting')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center">
                                                            <span>Notes</span>
                                                            <InfoTooltip id="info-notes" text="Any extra rules or reminders for the agent" />
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            placeholder="e.g. Never make up information. Ask only one question at a time."
                                                            value={formdata.prompt_notes || ''}
                                                            onChange={(e) => handlefieldvalue('prompt_notes', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    {touchedFields['prompt_notes'] && formdata.prompt_notes && (
                                                            <div className="d-flex justify-content-end mt-n2 mb-3">
                                                                {originalTexts['prompt_notes']===undefined && (<Button size="sm" variant="outline-primary" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={()=>handleSuggestClick('prompt_notes', 'Notes')} disabled={loadingSuggestion === 'prompt_notes'}>
                                                                    {loadingSuggestion === 'prompt_notes' ? (
                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                    ) : (
                                                                        <i className="bi bi-stars"></i>
                                                                    )} Suggest
                                                                </Button>)}
                                                                {originalTexts['prompt_notes'] !== undefined && (
                                                                    <Button size="sm" variant="outline-secondary" className="ms-2" style={{borderRadius: '20px', padding: '2px 10px', fontSize: '12px'}} onClick={() => handleUndoSuggest('prompt_notes')} title="Revert">
                                                                        <i className="bi bi-arrow-90deg-left"></i>  Revert
                                                                    </Button>
                                                                )}
                                                            </div>
                                                    )}
                                                </Col>
                                            </Row>

                                        </div>
                                    )}
                                    {instructionSubTab === 'final' && (
                                        <div>
                                            <div className="mb-3 p-2" style={{ border: '1px dashed #ccc', borderRadius: 4, background: '#fbfbfb' }}>
                                                <strong>Dynamic Variables:</strong>
                                                <div style={{ marginTop: 2 }}>
                                                    <code style={{ marginRight: 10 }}>[Person_Name]</code>
                                                    <code style={{ marginRight: 10 }}>[Company_Name]</code>
                                                    <code style={{ marginRight: 10 }}>[Person_Role]</code>
                                                </div>

                                                <strong style={{ marginTop: 2 }} className={"mt-6"}>Tools:</strong>
                                                <div style={{ marginTop: 2 }}>
                                                    <code style={{ marginRight: 10 }}>send_email</code>
                                                </div>
                                            </div>

                                            <Form.Group className="mb-3" >
                                                <Form.Label>Final Assembled Prompt <span className="text-muted" style={{ fontSize: 12 }}>(Generated from sections, but editable)</span></Form.Label>
                                                <Form.Control required name='prompt' as="textarea" rows={10} placeholder={placeholders.prompt} value={formdata.prompt} onChange={(e) => handlefieldvalue('prompt', e.target.value)} />
                                            </Form.Group>

                                            <Form.Group className="mb-3 mt-3" >
                                                <Form.Label>First Message</Form.Label>
                                                <Form.Control name='firstmessage' placeholder={placeholders.firstmessage} value={formdata.firstmessage || ''} onChange={(e) => handlefieldvalue('firstmessage', e.target.value)} type="text" />
                                            </Form.Group>
                                        </div>
                                    )}
                                    {instructionSubTab === 'template' && (
                                        <div className="p-3 bg-light border rounded">
                                            <h6>Base Prompt Template</h6>
                                            <p className="text-muted" style={{ fontSize: '12px' }}>This is the base template used to generate the final prompt. The placeholders (e.g. [Role], [Objectives]) are replaced with the values you fill in the Prompt Builder tab.</p>
                                            <pre style={{ fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                                                {formdata.prompt_template || PROMPT_TEMPLATE}
                                            </pre>
                                        </div>
                                    )}
                                    </div>

                                    {instructionSubTab === 'builder' && (
                                        <div className="d-flex justify-content-end align-items-center pt-3 border-top" style={{ flexShrink: 0, background: '#fff' }}>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                onClick={handleGeneratePrompt}
                                            >
                                                Generate Final Prompt
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="Knowledge Base">
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Knowledge Base Files</Form.Label>
                                            <Form.Control name='file' ref={fileRef} placeholder={placeholders.file} type="file" multiple onChange={handleFilesChange} />
                                                {selectedFiles.length > 0 && (
                                                    <div className="mt-2">
                                                        <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                                            {selectedFiles.map((f, i) => (
                                                                <li key={i}>{f.name} ({Math.round(f.size/1024)} KB)</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {agentFiles.length > 0 && (
                                                    <div className="mt-2">
                                                        <strong>Uploaded Files:</strong>
                                                        <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                                            {agentFiles.map((f, i) => (
                                                                                                                        <li key={i} className="d-flex align-items-center" style={{justifyContent: 'flex-start', gap: '0.5rem'}}>
                                                                                                                                <span style={{flexGrow:1}}>{f.name} ({Math.round(f.size/1024)} KB)</span>
                                                                                                                                <Button size="sm" variant="link" onClick={() => deleteAgentFile(f.name)} title={`Delete ${f.name}`} style={{padding:'0.15rem', color:'#dc3545'}}>
                                                                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x-circle" viewBox="0 0 16 16">
                                                                                                                                            <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zM8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0z"/>
                                                                                                                                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                                                                                                                                        </svg>
                                                                                                                                </Button>
                                                                                                                        </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Tab.Pane>
                            <Tab.Pane eventKey="Actions">
                                <Tab.Container defaultActiveKey="Configuration">
                                    <Nav variant="pills" className="mb-3 mt-2" style={{ fontSize: '0.9rem' }}>
                                        <Nav.Item>
                                            <Nav.Link eventKey="Configuration" style={{ padding: '0.4rem 1rem' }}>Configuration</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="Overview" style={{ padding: '0.4rem 1rem' }}>Overview</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="Meeting" style={{ padding: '0.4rem 1rem' }}>Meeting</Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                    <Tab.Content style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '4px', overflowY: 'auto', maxHeight: '400px' }}>
                                        <Tab.Pane eventKey="Configuration">
                                            <div className="mb-3">
                                                <Form.Check
                                                    type="switch"
                                                    id="enable-calendar-switch-actions"
                                                    label="Enable calendar integration for this agent"
                                                    checked={!!formdata.enable_calendar}
                                                    onChange={(e) => handlefieldvalue('enable_calendar', e.target.checked)}
                                                />
                                                {!!formdata.enable_calendar && (
                                                    <div className="mt-3 p-3 mb-4" style={{ border: '1px solid #eee', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Cal.com Username</Form.Label>
                                                            <Form.Control
                                                                name='cal_username'
                                                                placeholder="e.g. john-doe"
                                                                value={formdata.cal_username || ''}
                                                                onChange={(e) => handlefieldvalue('cal_username', e.target.value)}
                                                                type="text"
                                                            />
                                                        </Form.Group>
                                                        <Form.Group className="mb-0">
                                                            <Form.Label>Event Type Slug</Form.Label>
                                                            <Form.Control
                                                                name='cal_event_type_slug'
                                                                placeholder="e.g. 15min"
                                                                value={formdata.cal_event_type_slug || ''}
                                                                onChange={(e) => handlefieldvalue('cal_event_type_slug', e.target.value)}
                                                                type="text"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mb-3 mt-4">
                                                <Form.Check
                                                    type="switch"
                                                    id="enable-email-switch-actions"
                                                    label="Enable email integration for this agent"
                                                    checked={!!formdata.enable_email}
                                                    onChange={(e) => handlefieldvalue('enable_email', e.target.checked)}
                                                />
                                            </div>

                    
                                            {!!formdata.enable_email && (
                                               <div className="mt-3 p-3 mb-4" style={{ border: '1px solid #eee', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Display Name</Form.Label>
                                                            <Form.Control name='displayname' value={formdata.displayname || ''} placeholder='e.g. Wowleads Team' onChange={(e) => handlefieldvalue('displayname', e.target.value)} type="text" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Host</Form.Label>
                                                            <Form.Control required name='host' value={formdata.host || ''} placeholder='' onChange={(e) => handlefieldvalue('host', e.target.value)} type="text" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Port</Form.Label>
                                                            <Form.Control required name='port' value={formdata.port || ''} placeholder='' onChange={(e) => handlefieldvalue('port', e.target.value)} type="number" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Auth User</Form.Label>
                                                            <Form.Control required name='auth_user' value={formdata.auth_user || ''} placeholder='' onChange={(e) => handlefieldvalue('auth_user', e.target.value)} type="email" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>Auth Password</Form.Label>
                                                            <Form.Control required name='auth_password' value={formdata.auth_password || ''} placeholder='' onChange={(e) => handlefieldvalue('auth_password', e.target.value)} type="password" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>From</Form.Label>
                                                            <Form.Control required name='sender_mail' value={formdata.sender_mail || ''} placeholder='' onChange={(e) => handlefieldvalue('sender_mail', e.target.value)} type="email" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>CC</Form.Label>
                                                            <Form.Control required name='cc' value={formdata.cc || ''} placeholder='' onChange={(e) => handlefieldvalue('cc', e.target.value)} type="email" />
                                                        </Form.Group>
                                                    </Row>
                                                    <Row>
                                                        <Form.Group className="mb-3" >
                                                            <Form.Label>BCC</Form.Label>
                                                            <Form.Control required name='bcc' value={formdata.bcc || ''} placeholder='' onChange={(e) => handlefieldvalue('bcc', e.target.value)} type="email" />
                                                        </Form.Group>
                                                    </Row>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* ── OVERVIEW TAB ── */}
                                        <Tab.Pane eventKey="Overview">
                                            <div className="mb-3">
                                                <Form.Check
                                                    type="switch"
                                                    id="enable-overview-mail-switch"
                                                    label="Enable automated overview mail"
                                                    checked={!!formdata.enable_overview}
                                                    onChange={(e) => handlefieldvalue('enable_overview', e.target.checked)}
                                                />
                                                {!!formdata.enable_overview && (
                                                    <div className="mt-3">
                                                        {/* Email Template Selector */}
                                                        {!!formdata.enable_email && (
                                                            <Row className="mb-3">
                                                                <Form.Group>
                                                                    <Form.Label className="fw-semibold">Select Email Overview Template</Form.Label>
                                                                    {emailOverviewTemplates.length === 0 ? (
                                                                        <div className="p-2 text-muted border rounded bg-light" style={{ fontSize: '0.85rem' }}>
                                                                            No Email overview templates found.
                                                                            <a href="/campaigns/voice-campaigns/templates" target="_blank" rel="noreferrer" className="ms-2 text-primary">Create one →</a>
                                                                        </div>
                                                                    ) : (
                                                                        <Form.Control
                                                                            as="select"
                                                                            name='overview_email_template_id'
                                                                            value={formdata.overview_email_template_id || ''}
                                                                            onChange={(e) => handlefieldvalue('overview_email_template_id', e.target.value)}
                                                                        >
                                                                            <option value="">— Select Email Template —</option>
                                                                            {emailOverviewTemplates?.map(t => (
                                                                                <option key={t.id} value={t.id}>{t.name}{t.subject ? ` (Subject: ${t.subject.substring(0, 30)})` : ''}</option>
                                                                            ))}
                                                                        </Form.Control>
                                                                    )}
                                                                </Form.Group>
                                                            </Row>
                                                        )}

                                                        {/* WhatsApp Template Selector */}
                                                        <Row className="mb-3">
                                                            <Form.Group>
                                                                <Form.Label className="fw-semibold">Select WhatsApp Overview Template</Form.Label>
                                                                {whatsappOverviewTemplates.length === 0 ? (
                                                                    <div className="p-2 text-muted border rounded bg-light" style={{ fontSize: '0.85rem' }}>
                                                                        No WhatsApp overview templates found.
                                                                        <a href="/campaigns/voice-campaigns/templates" target="_blank" className="ms-2 text-primary">Create one →</a>
                                                                    </div>
                                                                ) : (
                                                                    <Form.Control
                                                                        as="select"
                                                                        name='overview_whatsapp_template_id'
                                                                        value={formdata.overview_whatsapp_template_id || ''}
                                                                        onChange={(e) => handlefieldvalue('overview_whatsapp_template_id', e.target.value)}
                                                                    >
                                                                        <option value="">— Select WhatsApp Template —</option>
                                                                        {whatsappOverviewTemplates?.map(t => (
                                                                            <option key={t.id} value={t.id}>{t.name}</option>
                                                                        ))}
                                                                    </Form.Control>
                                                                )}
                                                            </Form.Group>
                                                        </Row>

                                                        <div className="text-end">
                                                            <a href="/campaigns/voice-campaigns/templates" target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.8rem' }}>
                                                                <i className="bi bi-box-arrow-up-right me-1"></i>Manage Templates
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Tab.Pane>

                                        {/* ── MEETING TAB ── */}
                                        <Tab.Pane eventKey="Meeting">
                                            <div className="mb-3">
                                                <Form.Check
                                                    type="switch"
                                                    id="enable-meeting-switch"
                                                    label="Enable meeting confirmation follow-up"
                                                    checked={!!formdata.enable_meeting}
                                                    onChange={(e) => handlefieldvalue('enable_meeting', e.target.checked)}
                                                />
                                                {!!formdata.enable_meeting && (
                                                    <div className="mt-3">
                                                        {/* Email Template Selector */}
                                                        {!!formdata.enable_email && (
                                                            <Row className="mb-3">
                                                                <Form.Group>
                                                                    <Form.Label className="fw-semibold">Select Email Meeting Template</Form.Label>
                                                                    {emailMeetingTemplates.length === 0 ? (
                                                                        <div className="p-2 text-muted border rounded bg-light" style={{ fontSize: '0.85rem' }}>
                                                                            No Email meeting templates found.
                                                                            <a href="/campaigns/voice-campaigns/templates" target="_blank" rel="noreferrer" className="ms-2 text-primary">Create one →</a>
                                                                        </div>
                                                                    ) : (
                                                                        <Form.Control
                                                                            as="select"
                                                                            name='meeting_email_template_id'
                                                                            value={formdata.meeting_email_template_id || ''}
                                                                            onChange={(e) => handlefieldvalue('meeting_email_template_id', e.target.value)}
                                                                        >
                                                                            <option value="">— Select Email Template —</option>
                                                                            {emailMeetingTemplates?.map(t => (
                                                                                <option key={t.id} value={t.id}>{t.name}{t.subject ? ` (Subject: ${t.subject.substring(0, 30)})` : ''}</option>
                                                                            ))}
                                                                        </Form.Control>
                                                                    )}
                                                                </Form.Group>
                                                            </Row>
                                                        )}

                                                        {/* WhatsApp Template Selector */}
                                                        <Row className="mb-3">
                                                            <Form.Group>
                                                                <Form.Label className="fw-semibold">Select WhatsApp Meeting Template</Form.Label>
                                                                {whatsappMeetingTemplates.length === 0 ? (
                                                                    <div className="p-2 text-muted border rounded bg-light" style={{ fontSize: '0.85rem' }}>
                                                                        No WhatsApp meeting templates found.
                                                                        <a href="/campaigns/voice-campaigns/templates" target="_blank" rel="noreferrer" className="ms-2 text-primary">Create one →</a>
                                                                    </div>
                                                                ) : (
                                                                    <Form.Control
                                                                        as="select"
                                                                        name='meeting_whatsapp_template_id'
                                                                        value={formdata.meeting_whatsapp_template_id || ''}
                                                                        onChange={(e) => handlefieldvalue('meeting_whatsapp_template_id', e.target.value)}
                                                                    >
                                                                        <option value="">— Select WhatsApp Template —</option>
                                                                        {whatsappMeetingTemplates?.map(t => (
                                                                            <option key={t.id} value={t.id}>{t.name}</option>
                                                                        ))}
                                                                    </Form.Control>
                                                                )}
                                                            </Form.Group>
                                                        </Row>

                                                        <div className="text-end">
                                                            <a href="/campaigns/voice-campaigns/templates" target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.8rem' }}>
                                                                <i className="bi bi-box-arrow-up-right me-1"></i>Manage Templates
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Tab.Container>
                            </Tab.Pane>

                            
                        </Tab.Content>

                    </Tab.Container>
                    <div className='p-2 d-flex justify-content-end'>
                        <Button type="reset" className='mx-1' variant="secondary"  >
                            Cancel
                        </Button>
                        <Button type="submit" className={`mx-1`} title={props.modifymode ? 'Modify this Agent' : 'Create new Agent'} variant="primary" >
                            Save
                        </Button>
                    </div>
                </Form>
            </Container>
        </>
    )
}

export default AForm